import { GridPaginationModel } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector, useCallback } from 'hooks';
import { AnalysisECGStaus, DataStatus, ViewMode } from 'types/enums';
import { studyActions } from 'utils/store';

type Response = {
  isLoading: boolean;
  handleRowClick: (waveformId: string, status: string) => void;
  onPaginationChangeHandler: (paginationModel: GridPaginationModel) => void;
  updateStudyWaveforms: () => void;
  onViewModeChange: (mode: ViewMode) => void;
  onFacilityChange: (facilityId: string) => void;
};

const useStudyWaveforms = (): Response => {
  const dispatch = useAppDispatch();
  const { status } = useAppSelector(({ study }) => study);
  const isLoading = status === DataStatus.LOADING;

  const updateStudyWaveforms = useCallback(() => dispatch(studyActions.getPatientsStudyData()), [dispatch]);

  const handleRowClick = useCallback(
    (waveformId: string, status: string) => {
      const isAnalyzeExist = status === AnalysisECGStaus.PAIRED || status === AnalysisECGStaus.EXPORTED;
      dispatch(studyActions.getDetailedEcgData({ waveformId, isAnalyzeExist }));
    },
    [dispatch],
  );

  const onPaginationChangeHandler = useCallback(
    (paginationModel: GridPaginationModel) => {
      const { page, pageSize } = paginationModel;
      const skip = page * pageSize;
      dispatch(studyActions.setTableNavigation({ limit: pageSize, skip }));
      updateStudyWaveforms();
    },
    [updateStudyWaveforms, dispatch],
  );

  const onViewModeChange = useCallback(
    async (appMode: ViewMode) => {
      dispatch(studyActions.setStudyViewMode(appMode));
      dispatch(studyActions.clearStudyData());
      dispatch(studyActions.getStudyStatistic());
      updateStudyWaveforms();
    },
    [dispatch, updateStudyWaveforms],
  );

  const onFacilityChange = useCallback(
    (studyFacilityId: string) => {
      if (!studyFacilityId) {
        dispatch(studyActions.clearStudyData());
        dispatch(studyActions.getStudyStatistic());

        return;
      }
      dispatch(studyActions.setStudyFacilityId(studyFacilityId));
      updateStudyWaveforms();
    },
    [dispatch, updateStudyWaveforms],
  );

  return {
    isLoading,
    handleRowClick,
    onPaginationChangeHandler,
    updateStudyWaveforms,
    onViewModeChange,
    onFacilityChange,
  };
};

export { useStudyWaveforms };
